import EntityType from "../../enum/EntityType";
import AnyColor from "../../types/AnyColor";
import {
  ChatActivityListRecord,
  ChatCompanyListRecord,
  ChatDealListRecord,
  ChatPersonListRecord,
  ChatRecordListContent,
} from "../../types/chat/ChatContent";

import { CONTEXT_ENTITY_TYPES, isRecord } from "./chatWireGuards";

const asBoolean = (value: unknown): boolean | undefined =>
  typeof value === "boolean" ? value : undefined;

const asNumber = (value: unknown): number | undefined =>
  typeof value === "number" ? value : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

// The wire sends a colour as a plain string; AnyColor is a branded type the UI components
// require. The cast belongs here, at the parse boundary, and nowhere downstream.
const asColor = (value: unknown): AnyColor | null =>
  typeof value === "string" ? (value as AnyColor) : null;

// A nested reference the card links to, so its id has to be real. Returns undefined when it
// is not, which reads to the row as "no parent company" rather than an unclickable one.
const asIdentifiedRef = (value: unknown): { id: number; name?: string } | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }
  const id = asNumber(value.id);
  return id === undefined ? undefined : { id, name: asString(value.name) };
};

const parseCompanyRow = (row: Record<string, unknown>, id: number): ChatCompanyListRecord => ({
  id,
  distance: asNumber(row.distance),
  email: asString(row.email),
  name: asString(row.name),
  phone: asString(row.phone),
});

const parsePersonRow = (row: Record<string, unknown>, id: number): ChatPersonListRecord => ({
  id,
  account: asIdentifiedRef(row.account),
  distance: asNumber(row.distance),
  email: asString(row.email),
  name: asString(row.name),
  phone: asString(row.phone),
});

const parseActivityRow = (row: Record<string, unknown>, id: number): ChatActivityListRecord => {
  const activityType = isRecord(row.crmActivityType) ? row.crmActivityType : undefined;
  const activityTypeId = asNumber(activityType?.id);
  const activityTypeName = asString(activityType?.name);
  return {
    id,
    // Both id and name are required by ActivityTypeIcon's own prop type, so a half-formed
    // type object is dropped rather than passed on for the icon to fail on.
    completed: asBoolean(row.completed),
    crmActivityType:
      activityTypeId !== undefined && activityTypeName !== undefined
        ? { id: activityTypeId, color: asColor(activityType?.color), name: activityTypeName }
        : undefined,
    distance: asNumber(row.distance),
    name: asString(row.name),
    startAt: asString(row.startAt),
  };
};

const parseDealRow = (row: Record<string, unknown>, id: number): ChatDealListRecord => {
  const stage = isRecord(row.stage) ? row.stage : undefined;
  const user = isRecord(row.user) ? row.user : undefined;
  return {
    id,
    amount: asNumber(row.amount),
    closingDate: asString(row.closingDate),
    distance: asNumber(row.distance),
    funnel: asIdentifiedRef(row.funnel),
    name: asString(row.name),
    // The stage's own id is optional on the wire, unlike a funnel's, and the card never
    // navigates by it — so this one is not built through asIdentifiedRef.
    stage: stage
      ? {
          id: asNumber(stage.id),
          color: asColor(stage.color),
          name: asString(stage.name),
          type: asString(stage.type),
        }
      : undefined,
    user: user ? { id: asNumber(user.id), fullName: asString(user.fullName) } : undefined,
  };
};

// One malformed row must not blank a card of ten, so a row without a usable id is dropped
// with a warning and its siblings still render.
const parseRows = <T>(
  records: unknown[],
  parseRow: (row: Record<string, unknown>, id: number) => T,
  warnings: string[]
): T[] =>
  records.reduce<T[]>((accumulator, row) => {
    const id = isRecord(row) ? asNumber(row.id) : undefined;
    if (!isRecord(row) || id === undefined) {
      warnings.push("Dropped a RECORD_LIST row with no numeric id");
      return accumulator;
    }
    accumulator.push(parseRow(row, id));
    return accumulator;
  }, []);

const parseRecordList = (
  payload: Record<string, unknown>,
  warnings: string[]
): ChatRecordListContent | undefined => {
  const { records, total } = payload;
  if (!Array.isArray(records) || typeof total !== "number") {
    return undefined;
  }
  // The same allowlist a message's context entity is narrowed against: the four types a list can
  // carry are the four a message can be about, and a second copy would drift from this one.
  const entityType = CONTEXT_ENTITY_TYPES.find((candidate) => candidate === payload.entityType);
  switch (entityType) {
    case EntityType.ACTIVITY:
      return { entityType, records: parseRows(records, parseActivityRow, warnings), total };
    case EntityType.COMPANY:
      return { entityType, records: parseRows(records, parseCompanyRow, warnings), total };
    case EntityType.DEAL:
      return { entityType, records: parseRows(records, parseDealRow, warnings), total };
    case EntityType.PERSON:
      return { entityType, records: parseRows(records, parsePersonRow, warnings), total };
    default:
      return undefined;
  }
};

export default parseRecordList;
